import { Color3, type Mesh, MeshBuilder, StandardMaterial, Vector3 } from "@babylonjs/core";
import { HeroId, Slot, hv } from "@rivalwatch/shared";
import { FX, PK, V } from "../../../../shared/src/heroes/roster/winter-shoulder.ts";
import { baked } from "../../audio/bake.ts";
import { bell, brass, click, clinks, drive, fm, later, noise, thump, tuba, tone, trim, whoosh } from "../../audio/synth.ts";
import type { Cue } from "../../audio/engine.ts";
import type { ClientHero } from "../types.ts";

type Gear = { pistol: Mesh; muzzle: Mesh; cylinder: Mesh; arm: Mesh; shoulder: Mesh; star: Mesh; hair: Mesh; mask: Mesh };

const sidearm: Cue = baked("winter-shoulder:sidearm", () => trim(-0.5, (o) => {
  click(o, 0.65, 2400, 0.008);
  thump(o, 0.82, 118, 42, 0.18);
  noise(drive(o, 2.2), { color: "pink", type: "lowpass", f: 2600, f1: 420, gain: 0.55, d: 0.22 });
  clinks(later(o, 0.07), 2, 0.05, 0.06, 1200, 3000);
  return 0.5;
}), { variants: 4, jitter: 0.012 });
const hookCue: Cue = trim(7.2, (o) => {
  clinks(o, 10, 0.09, 0.22, 900, 3600);
  whoosh(o, 0.64, 500, 2100, 0.25);
  fm(o, { f: 260, ratio: 2.2, index: 4, index1: 0.2, gain: 0.12, d: 0.3 });
  return 0.65;
});
const fistCue: Cue = trim(-1.8, (o) => {
  thump(o, 0.92, 90, 34, 0.24);
  noise(drive(o, 2.6), { color: "pink", type: "bandpass", f: 900, f1: 2200, gain: 0.62, d: 0.22 });
  fm(later(o, 0.04), { f: 120, ratio: 3.1, index: 5, index1: 0, gain: 0.13, d: 0.3 });
  return 0.55;
});
const currentCue: Cue = trim(11, (o) => {
  click(o, 0.42, 5200, 0.003);
  tone(o, { type: "square", f: 1300, f1: 500, glide: 0.16, gain: 0.12, d: 0.18 });
  noise(o, { color: "crackle", type: "highpass", f: 3600, gain: 0.5, d: 0.22 });
  return 0.42;
});
const krakenCue: Cue = trim(-7, (o) => {
  thump(o, 1.05, 74, 26, 0.55);
  noise(drive(o, 2.5), { color: "brown", type: "lowpass", f: 900, f1: 180, gain: 0.85, d: 0.65 });
  for (const s of [0, 6, 7]) bell(later(o, 0.12), 293.66 * 2 ** (s / 12), 0.55, 0.55);
  return 1.2;
});
const reloadCue = (seconds: number): Cue => trim(9, (o) => {
  click(o, 0.28, 1100, 0.012, seconds * 0.2);
  clinks(o, 3, 0.06, seconds * 0.45, 900, 2600);
  click(o, 0.45, 2100, 0.008, seconds * 0.82);
  return seconds + 0.15;
});
const metalImpact: Cue = trim(6, (o) => {
  fm(o, { f: 700, ratio: 2.41, index: 5, index1: 0.2, gain: 0.16, d: 0.25 });
  thump(o, 0.45, 140, 52, 0.12);
  return 0.35;
});
const boom = (radius: number): Cue => trim(7, (o) => {
  thump(o, 1.0, 90, 30, 0.38);
  noise(drive(o, 2), { color: "pink", type: "lowpass", f: 2300, f1: 260, gain: 0.65, d: 0.42 + radius * 0.03 });
  return 0.9;
});
const theme = {
  bpm: 86,
  octave: -1,
  voice: tuba,
  double: (o, f, d, v) => void brass(later(o, 0.03), f * 2, d * 0.65, v * 0.38),
  notes: [[0, 1], [2, 0.5], [7, 0.5], [6, 1], [4, 1.5]],
} satisfies ClientHero["audio"]["theme"];

const icons = {
  gun: "M4 13h10l6-3v6l-6-3H4zM6 16v3",
  hook: "M5 5c9 0 14 4 10 9-2 3-7 2-7-2",
  fist: "M8 6h8v7l-4 5-4-5zM8 9H5m11 0h3",
  current: "M13 2L6 13h6l-1 9 7-12h-6z",
  kraken: "M12 3v18M6 8c4 4 8 4 12 0M5 16c5-4 9-4 14 0",
};

const hero: ClientHero<Gear> = {
  id: HeroId.WinterShoulder,
  bulk: 1.08,
  shape: { head: 0.98, torso: 1.08, arms: 1.13, legs: 1.02 },
  gear(g) {
    const { scene, add, rig, toon, glow, R, H } = g;
    const chrome = toon("#b7c2d0");
    if (rig.parts.armL) rig.parts.armL.material = chrome;
    const pistol = add(MeshBuilder.CreateBox("red-star-sidearm", { width: 0.2, height: 0.22, depth: 0.72 }, scene), rig.handR, toon("#0b1118"));
    pistol.position.z = -0.25;
    const cylinder = add(MeshBuilder.CreateCylinder("sidearm-cylinder", { diameter: 0.18, height: 0.18, tessellation: 10 }, scene), rig.handR, toon("#6c7a89"));
    cylinder.rotation.z = Math.PI / 2;
    cylinder.position.set(0, -0.02, -0.2);
    const muzzle = add(MeshBuilder.CreateCylinder("sidearm-muzzle", { diameter: 0.12, height: 0.18, tessellation: 10 }, scene), rig.handR, glow("#c4d1df"));
    muzzle.rotation.x = Math.PI / 2;
    muzzle.position.z = -0.68;
    const arm = add(MeshBuilder.CreateBox("chrome-left-arm", { width: R * 0.68, height: H * 0.36, depth: R * 0.52 }, scene), rig.armL, chrome);
    arm.position.y = -0.24;
    const shoulder = add(MeshBuilder.CreateSphere("metal-shoulder", { diameter: R * 0.95, segments: 12 }, scene), rig.armL, glow("#9fb4c8", 0.45));
    shoulder.position.y = 0.02;
    const star = add(MeshBuilder.CreateTorus("cold-red-star", { diameter: R * 0.62, thickness: 0.025, tessellation: 5 }, scene), rig.armL, glow("#c33b3b"));
    star.position.set(0, -0.18, -R * 0.35);
    const hair = add(MeshBuilder.CreateBox("long-hair", { width: R * 0.82, height: 0.75, depth: 0.08 }, scene), rig.head, toon("#2b2018"));
    hair.position.set(0, -0.2, R * 0.45);
    const mask = add(MeshBuilder.CreateBox("half-mask", { width: R * 0.9, height: 0.28, depth: 0.05 }, scene), rig.head, toon("#0b1118"));
    mask.position.set(0, 0, -R * 0.55);
    return { pistol, muzzle, cylinder, arm, shoulder, star, hair, mask };
  },
  animate(gear, f) {
    const charge = f.v[V.hookCharge];
    const kraken = f.v[V.ultPhase] > 0 || f.ultActive;
    gear.shoulder.scaling.setAll(1 + charge * 0.35 + (kraken ? 0.25 : 0));
    gear.star.rotation.z += f.dt * (kraken ? 8 : 2);
    gear.muzzle.visibility = f.action === 1 ? 1 : 0.6;
    gear.cylinder.rotation.y += f.dt * (f.action === 1 ? 16 : 2);
    if (f.v[V.ultPhase] === 1) return "holding-both";
    if (f.moveKind >= 16) return "attack-kick-right";
    if (f.action === 4 || f.action === 5) return "interact-left";
    return undefined;
  },
  hud(c, eid) {
    return [
      { label: "Hook charge", value: hv(c, eid, V.hookCharge), color: "#c4d1df" },
      { label: "Cull window", value: hv(c, eid, V.cullTicks) / (5 * 60), color: "#9fb4c8" },
      { label: "Recast", value: hv(c, eid, V.ultRecast) / (8 * 60), color: "#d7ecff" },
    ];
  },
  icons: { [Slot.Primary]: icons.gun, [Slot.Secondary]: icons.hook, [Slot.Ability1]: icons.fist, [Slot.Ability2]: icons.current, [Slot.Ultimate]: icons.kraken, passive: "M6 5h12M8 9h8M10 13h4M12 17v4" },
  projectiles: {
    [PK.Round](scene) {
      const m = MeshBuilder.CreateSphere("shoulder-round", { diameter: 0.2, segments: 8 }, scene);
      const mat = new StandardMaterial("round-mat", scene);
      mat.emissiveColor = Color3.FromHexString("#c4d1df");
      mat.disableLighting = true;
      m.material = mat;
      return { gear: m, update: (mesh: Mesh, frame) => mesh.position.set(frame.pos.x, frame.pos.y, frame.pos.z), dispose: () => m.dispose() };
    },
    [PK.Hook](scene) {
      const m = MeshBuilder.CreateCylinder("bionic-hook", { diameter: 0.13, height: 0.78, tessellation: 8 }, scene);
      const claw = MeshBuilder.CreateTorus("hook-claw", { diameter: 0.28, thickness: 0.035, tessellation: 12 }, scene);
      claw.parent = m;
      claw.position.y = 0.38;
      return {
        gear: m,
        update(mesh: Mesh, frame) {
          mesh.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
          mesh.lookAt(mesh.position.add(new Vector3(frame.dir.x, frame.dir.y, frame.dir.z)));
          mesh.rotation.x += Math.PI / 2;
        },
        dispose: () => m.dispose(),
      };
    },
  },
  audio: {
    weight: "mid",
    fire: () => ({ key: "winter-shoulder:sidearm", cue: sidearm }),
    reload: reloadCue,
    ability: { [Slot.Secondary]: hookCue, [Slot.Ability1]: fistCue, [Slot.Ability2]: currentCue, [Slot.Ultimate]: krakenCue },
    impacts: { [FX.Arm]: metalImpact, [FX.Round]: metalImpact },
    explosions: { [FX.Round]: boom, [FX.Ult]: boom },
    theme,
    themeTrim: { heroic: -10.5, hostile: -10.8, tease: -15.0 },
  },
};

export default hero;
